// glonet.ai /ops — the voice agent that runs the day.
// AI Ops Platform build-order item 1 (docs/ops/ai-ops-implementation-plan.html).
//
// "Nobody should open a tab to find out what to do. The agent tells you.
//  You say yes or no. It remembers."
//
// The shape, and why:
//   · The CARDS ARE THE PRODUCT. Voice is an accelerant, never a gate — the
//     page paints everything before the mic is even asked for, and a denied
//     mic changes nothing but the silence. Nothing here is voice-only.
//   · THREE CARDS AT MOST on screen at once. If there are more, the count is
//     shown and the agent says the rest out loud. A wall of cards is a tab.
//   · "THAT'S WRONG" ON EVERY CARD. If people cannot say no to it they stop
//     opening it, and rejections are the most useful data we get.
//   · One idea, one or two buttons, then gone. A card that is answered
//     disappears; it does not turn into a checkmark you have to look past.
//
// Timing is measured, not felt: gvTimedTools puts every client-tool dispatch
// against the 500ms law and gvModeRecorder puts every listening->speaking gap
// against 1000ms, landing on window.__GLONET_*_TIMINGS__ for the flow runner.
//
// ?preview=1 renders clearly-labeled SAMPLE cards with no backend and no mic —
// that is also what the offline harness and the screenshots use.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import { GLONET_OPS_AGENT_ID } from "../config/glonetai";
import {
  gvMark, gvModeRecorder, gvTel, gvTelReset, gvTimeSpan, gvTimedTools,
} from "../utils/voiceTelemetry";
import log from "../utils/logger";

const ACCENT = "#74a0f5";
const GREEN = "#57d9a4";
const AMBER = "#e0a35e";
const RED = "#e57373";
const MONO = "'JetBrains Mono',monospace";
const SERIF = "'Instrument Serif',serif";

const MAX_CARDS = 3;   // never more than three in a row — the design's own rule

const CARD_TONE = {
  plan: ACCENT, decision: AMBER, warning: RED, command: "#c39bf0", report: GREEN,
};

export const PREVIEW_BRIEF = {
  role: "brian", name: "Brian", mode: "live", away: false, covering_for: [],
  phase: { phase: "fill", label: "Fill the pond", launch_date: "2026-09-15",
           days_to_launch: 34, job: "Get people and affiliates in. Matching is off.",
           push: "Recruit. Warm up affiliates. Get profiles filled." },
  ops: { send_hold: false, drumbeat_enabled: true, brief_enabled: true },
  spoken: "Morning Brian. Two things need a yes or no, then the next three hours are "
    + "40 newsletter owners. Signups up 18% this week. Five affiliates are 61% of our "
    + "50,000 reach — talk to those five first.",
  trend: "Signups up 18% this week (13 vs about 11).",
  warnings: [],
  decisions: [
    { item_key: "app:9", card: "decision", title: "Wren Adeyemi's affiliate application",
      detail: "Claims 9,000 people. AI scores it 80. Real newsletter, checkable, "
        + "audience is operators — approve as Partner.", status: "open",
      payload: { kind: "affiliate_application", id: 9 } },
    { item_key: "held:payouts", card: "decision", title: "$612.30 is being held, not paid",
      detail: "3 payouts on hold (review-gate, self-referral-suspect). Nobody has been "
        + "told; the money waits for your call.", status: "open",
      payload: { kind: "held_payouts" } },
  ],
  commands: [
    { item_key: "req:4", card: "command", title: "Luis wants: Who joined but never filled a profile",
      detail: "For the launch-day list", status: "open",
      payload: { kind: "request", id: 4,
                 command: "In the Glonet database, list members who joined on or after "
                   + "2026-07-01 and have no matching profile (no attendee_inputs row with a "
                   + "non-empty objective and offering for event 188). Give me name, email and "
                   + "join date, newest first, and the total. Exclude the jamielannistergl "
                   + "test accounts." } },
  ],
  plan: [
    { item_key: "plan:outreach", card: "plan", title: "Message 40 newsletter and community owners",
      detail: "Use the Messages tab — pick the one that is converting best and log what you send.",
      target: 40, progress: 25, status: "started" },
    { item_key: "plan:stripe-chase", card: "plan",
      title: "Chase 3 affiliates with no payout setup",
      detail: "They cannot be paid until Stripe is done, and unpaid people stop sending.",
      target: 3, progress: null, status: "open" },
  ],
  report: { signups_7d: 13, signups_1d: 2, referred_7d: 6, members_total: 190,
            per_week: 4.2, affiliates: 12, affiliates_referring: 5, pending_applications: 2 },
  reach: { total_reach: 50000, top_n: 5, top_share: 0.61, with_numbers: 12, without_numbers: 3 },
  reach_line: "5 affiliates are 61% of our 50,000 claimed reach — talk to those 5 first.",
  scaling: { members: 190, per_week: 4.2, urgent: false,
             upcoming: [{ key: "rds:db.t4g.small", at: 1500,
                          title: "Upgrade the database (db.t4g.small)", weeks: 312,
                          detail: "Resize in the console off-peak.", minutes: 10 }] },
  logged_today: 25,
  jobs: [
    { job: "profile_gap", label: "Who joined but never filled a profile",
      params: [{ key: "since", label: "joined on or after", kind: "date" }] },
    { job: "affiliate_referrals", label: "Everyone one affiliate brought in",
      params: [{ key: "code", label: "affiliate code", kind: "code" }] },
    { job: "quiet_members", label: "Members who have gone quiet",
      params: [{ key: "days", label: "quiet for at least (days)", kind: "int", default: 30 }] },
  ],
};

// The {{brief}} the agent is given. One line per card, item_key first.
// Exported so a test can measure it: this string is re-sent on every turn, so
// its length is paid in time-to-first-audio, over and over.
export function buildBriefText(b, cap = 1600) {
  if (!b) return "(no brief)";
  const line = (i) => `${i.item_key} | ${i.title}`
    + (i.target != null && i.progress != null ? ` (${i.progress} of ${i.target})` : "")
    + (i.detail ? ` — ${String(i.detail).slice(0, 110)}` : "");
  const block = (name, items) =>
    (items || []).length ? `${name}:\n` + items.map((i) => "  " + line(i)).join("\n") + "\n" : "";
  const out = `phase: ${b.phase?.label || "unknown"}\n${b.trend || ""}\n`
    + block("WARNINGS", b.warnings)
    + block("DECISIONS", b.decisions)
    + block("TO RUN", b.commands)
    + block("PLAN", b.plan);
  return out.length > cap ? out.slice(0, cap) + "\n(more on screen)" : out;
}

const card = {
  background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 18, padding: "22px 24px",
};
const label = {
  fontFamily: MONO, fontSize: 11, letterSpacing: "0.2em",
  textTransform: "uppercase", color: "#6b6f78",
};
const btn = (primary, tone) => ({
  fontFamily: "inherit", fontSize: 13.5, fontWeight: 600, cursor: "pointer",
  padding: "9px 18px", borderRadius: 999, whiteSpace: "nowrap",
  color: primary ? "#0c0d10" : "#c3c6cc",
  background: primary ? (tone || ACCENT) : "transparent",
  border: primary ? "none" : "1px solid rgba(255,255,255,0.18)",
});

/* ————— one card ————— */
const Card = ({ item, onAct, busy }) => {
  const [rejecting, setRejecting] = useState(false);
  const [why, setWhy] = useState("");
  const [copied, setCopied] = useState(false);
  const tone = CARD_TONE[item.card] || ACCENT;
  const cmd = item.payload?.command;

  const copy = async () => {
    try { await navigator.clipboard.writeText(cmd); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch (e) { window.prompt("Copy this into Claude Code:", cmd); }
  };

  return (
    <section data-testid={`ops-card-${item.card}`} data-item-key={item.item_key}
             style={{ ...card, borderColor: `${tone}55`, marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
        <div style={{ ...label, color: tone }}>{item.card}</div>
        {item.claimed_by && (
          <div style={{ fontFamily: MONO, fontSize: 11, color: AMBER }}>
            {item.claimed_by} has this
          </div>
        )}
      </div>
      <div style={{ fontFamily: SERIF, fontSize: 25, lineHeight: 1.2, margin: "8px 0 6px" }}>
        {item.title}
      </div>
      {item.detail && (
        <div style={{ color: "#9a9ea7", fontSize: 14.5, lineHeight: 1.6, maxWidth: "72ch" }}>
          {item.detail}
        </div>
      )}

      {item.target != null && item.progress != null && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ fontFamily: MONO, fontSize: 26, color: tone }}>{item.progress}</span>
            <span style={{ color: "#7c818b", fontSize: 14 }}>of {item.target} so far</span>
          </div>
          <div style={{ height: 6, borderRadius: 999, background: "rgba(255,255,255,0.08)", marginTop: 8 }}>
            <div style={{ height: "100%", borderRadius: 999, background: tone,
              width: `${Math.min(100, (item.progress / Math.max(1, item.target)) * 100)}%`,
              transition: "width .5s" }} />
          </div>
        </div>
      )}

      {cmd && (
        <pre data-testid="ops-command-text"
             style={{ whiteSpace: "pre-wrap", fontFamily: MONO, fontSize: 12.5, lineHeight: 1.55,
               color: "#c3c6cc", background: "#101216", border: "1px solid rgba(255,255,255,0.1)",
               borderRadius: 10, padding: "12px 14px", margin: "14px 0 0" }}>{cmd}</pre>
      )}

      {rejecting ? (
        <div style={{ marginTop: 14 }}>
          <input autoFocus value={why} maxLength={200} onChange={(e) => setWhy(e.target.value)}
                 placeholder="what's wrong with it? (optional)" aria-label="what's wrong"
                 style={{ width: "100%", background: "rgba(255,255,255,0.05)", color: "#f1f2f4",
                   border: "1px solid rgba(255,255,255,0.16)", borderRadius: 10,
                   padding: "10px 12px", fontSize: 14, fontFamily: "inherit" }} />
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button style={btn(true, RED)} onClick={() => onAct(item, "reject", why)}>
              Drop it
            </button>
            <button style={btn(false)} onClick={() => setRejecting(false)}>Keep it</button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16, alignItems: "center" }}>
          {item.card === "plan" && (
            <>
              <button style={btn(true, tone)} disabled={busy}
                      onClick={() => onAct(item, item.status === "started" ? "done" : "start")}>
                {item.status === "started" ? "Done" : "Start"}
              </button>
              <button style={btn(false)} disabled={busy} onClick={() => onAct(item, "skip")}>Not today</button>
            </>
          )}
          {item.card === "decision" && (
            <>
              <button style={btn(true, tone)} disabled={busy} onClick={() => onAct(item, "claim")}>
                I'll take it
              </button>
              <button style={btn(false)} disabled={busy} onClick={() => onAct(item, "done")}>Handled</button>
              <button style={btn(false)} disabled={busy} onClick={() => onAct(item, "skip")}>Later</button>
            </>
          )}
          {item.card === "warning" && (
            <>
              <button style={btn(true, tone)} disabled={busy} onClick={() => onAct(item, "done")}>Fixed</button>
              <button style={btn(false)} disabled={busy} onClick={() => onAct(item, "snooze")}>Snooze</button>
            </>
          )}
          {item.card === "command" && (
            <>
              <button style={btn(true, tone)} onClick={copy}>{copied ? "Copied ✓" : "Copy"}</button>
              <button style={btn(false)} disabled={busy} onClick={() => onAct(item, "done")}>Done</button>
            </>
          )}
          {/* The most important button on the page. */}
          <button style={{ ...btn(false), border: "none", color: "#6b6f78", padding: "9px 6px" }}
                  data-testid="ops-thats-wrong"
                  onClick={() => setRejecting(true)}>
            that's wrong
          </button>
        </div>
      )}
    </section>
  );
};

export default function OpsBrief() {
  const navigate = useNavigate();
  const qs = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const preview = qs.get("preview") === "1";
  const noVoice = preview || qs.get("voice") === "0";

  const [state, setState] = useState(preview ? "ready" : "loading"); // loading|anon|denied|ready|error
  const [b, setB] = useState(preview ? PREVIEW_BRIEF : null);
  const [busy, setBusy] = useState(false);
  const [voice, setVoice] = useState("off");   // off|starting|live|failed
  const [speaking, setSpeaking] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [askJob, setAskJob] = useState(null);
  const [note, setNote] = useState(null);
  const sessionRef = useRef(null);
  const bRef = useRef(b);
  useEffect(() => { bRef.current = b; }, [b]);

  const load = useCallback(async () => {
    if (preview) return;
    try {
      await Auth.currentAuthenticatedUser();
    } catch (e) { setState("anon"); return; }
    try {
      const { default: api } = await import("../services/api");
      const { data } = await api.get("subscriptions/admin/gtm/brief", { timeout: 30000 });
      setB(data); setState("ready");
      gvTel("brief.loaded", { role: data.role, cards: (data.plan || []).length
        + (data.decisions || []).length + (data.warnings || []).length + (data.commands || []).length });
    } catch (e) {
      if (e.response?.status === 403) { setState("denied"); return; }
      log.error("ops brief failed", { error: e });
      setState("error");
    }
  }, [preview]);

  useEffect(() => { gvTelReset("ops"); load(); }, [load]);

  // ——— acting on a card ———
  const act = useCallback(async (item, verb, reason) => {
    // Optimistic: the card goes away the moment you answer it. A card that
    // lingers with a spinner is a card you have to look at twice.
    const drop = ["skip", "reject", "done"].includes(verb);
    if (drop) {
      setB((prev) => prev && {
        ...prev,
        plan: (prev.plan || []).filter((i) => i.item_key !== item.item_key),
        decisions: (prev.decisions || []).filter((i) => i.item_key !== item.item_key),
        warnings: (prev.warnings || []).filter((i) => i.item_key !== item.item_key),
        commands: (prev.commands || []).filter((i) => i.item_key !== item.item_key),
      });
    }
    if (preview) { setNote(verb === "reject" ? "Dropped (preview)." : "Noted (preview)."); return; }
    setBusy(true);
    try {
      const { default: api } = await import("../services/api");
      const { data } = await api.post("subscriptions/admin/gtm/brief/act",
        { item_key: item.item_key, verb, reason: reason || undefined });
      if (verb === "claim") {
        if (data.claimed === false) {
          setNote(`${data.held_by} has that one.`);
          setB((prev) => prev && { ...prev, decisions: (prev.decisions || []).map((i) =>
            i.item_key === item.item_key ? { ...i, claimed_by: data.held_by } : i) });
        } else {
          setNote("Yours — nobody else will pick it up.");
        }
      } else if (verb === "reject") {
        setNote("Dropped. It won't come back.");
      } else if (verb === "start") {
        setB((prev) => prev && { ...prev, plan: (prev.plan || []).map((i) =>
          i.item_key === item.item_key ? { ...i, status: "started" } : i) });
      }
      gvTel("card.act", { item_key: item.item_key, verb });
    } catch (e) {
      setNote("Couldn't save that — try again.");
      load();
    } finally { setBusy(false); }
  }, [preview, load]);

  const openRequest = useCallback(async (job, params, jobNote) => {
    if (preview) { setNote("Sent to Brian (preview)."); setAskJob(null); return; }
    try {
      const { default: api } = await import("../services/api");
      await api.post("subscriptions/admin/gtm/requests", { job, params, note: jobNote });
      setNote("On Brian's list. He'll run it and send the answer back.");
      setAskJob(null);
    } catch (e) {
      setNote(e.response?.data?.detail || "Couldn't send that.");
    }
  }, [preview]);

  // ——— the voice. Started only AFTER the cards are on screen. ———
  const startVoice = useCallback(async () => {
    if (noVoice || voice !== "off" || !bRef.current) return;
    setVoice("starting");
    try {
      const { Conversation } = await import("@elevenlabs/client");
      await navigator.mediaDevices.getUserMedia({ audio: true });
      gvMark("mic_granted");
      // Bluetooth headsets swap profiles when the mic engages and swallow the
      // greeting without this pause (same fix as VoiceOnboarding).
      await new Promise((r) => setTimeout(r, 1200));
      const recordMode = gvModeRecorder("ops");
      const brief = bRef.current;
      const session = await gvTimeSpan("session_connect", () => Conversation.startSession({
        agentId: GLONET_OPS_AGENT_ID,
        connectionType: "websocket",
        dynamicVariables: {
          person: brief.name || "there",
          opening: brief.spoken,
          // The agent's ENTIRE knowledge. Composed server-side, handed over
          // whole: the agent reads, it never queries.
          //
          // KEPT SMALL ON PURPOSE. A dynamic variable is re-sent with the
          // prompt on EVERY turn, so its size is paid per turn in
          // time-to-first-audio. The first live run shipped ~6 KB of JSON here
          // and measured a 6.4 s median response against a 2.3 s platform
          // norm; trimming to the fields the agent actually reads out is the
          // single biggest lever on how fast it feels.
          //
          // Lines, not JSON: fewer tokens for the same information, and the
          // item_key sits at the start of its line where the model will not
          // paraphrase it (a mangled key means a card the agent says it moved
          // and did not).
          brief: buildBriefText(brief),
        },
        clientTools: gvTimedTools("ops", {
          card_action: async (args) => {
            const key = (args && args.item_key) || "";
            const verb = (args && args.verb) || "";
            const all = [...(bRef.current?.plan || []), ...(bRef.current?.decisions || []),
                         ...(bRef.current?.warnings || []), ...(bRef.current?.commands || [])];
            const item = all.find((i) => i.item_key === key);
            // A miss used to be silent, which meant the agent could say
            // "dropped it" while the card sat there — the worst possible
            // failure for a thing whose whole job is being trusted. Record it.
            gvTel("card_action", { key, verb, matched: !!item,
                                   known: all.map((i) => i.item_key) });
            if (item) await act(item, verb, args && args.reason);
            else setNote(`Couldn't find that card — nothing changed.`);
          },
          open_request: async (args) => {
            const job = (args && args.job) || "";
            const spec = (bRef.current?.jobs || []).find((j) => j.job === job);
            if (spec) await openRequest(job, {}, (args && args.note) || undefined);
          },
        }),
        onModeChange: ({ mode }) => { recordMode(mode); setSpeaking(mode === "speaking"); },
        // The text rides the telemetry ring, truncated. Every other Glonet
        // voice surface records only the SOURCE, which is enough to time a
        // turn and useless for working out why the agent said something odd.
        // The ring is in-memory, capped and never leaves the tab, and this is
        // an admin talking to their own agent about their own numbers.
        onMessage: ({ source, message }) => {
          // Restart the response clock the moment the operator's words land,
          // so the number we report is the wait THEY felt — not the agent
          // being billed for their thinking time (voiceTelemetry.js).
          if (source === "user") recordMode.heard();
          gvTel("message", {
            surface: "ops", source, text: String(message || "").slice(0, 300),
          });
        },
        onDisconnect: () => { sessionRef.current = null; setVoice("off"); setSpeaking(false); },
      }));
      sessionRef.current = session;
      gvMark("session_live");
      setVoice("live");
    } catch (e) {
      // No mic, denied mic, or a failed session changes NOTHING but the
      // silence. Everything on this page still works.
      log.error("ops voice failed", { error: e });
      setVoice("failed");
    }
  }, [noVoice, voice, act, openRequest]);

  useEffect(() => () => {
    try { sessionRef.current?.endSession(); } catch (e) { /* closing */ }
  }, []);

  const stopVoice = async () => {
    try { await sessionRef.current?.endSession(); } catch (e) { /* closing */ }
    sessionRef.current = null; setVoice("off"); setSpeaking(false);
  };

  // ——— what is on screen ———
  const all = b ? [...(b.warnings || []), ...(b.decisions || []), ...(b.commands || []), ...(b.plan || [])] : [];
  const shown = showAll ? all : all.slice(0, MAX_CARDS);
  const hidden = all.length - shown.length;

  const page = { background: "#0c0d10", color: "#f1f2f4", minHeight: "100vh",
    fontFamily: "'Hanken Grotesk',system-ui,sans-serif", WebkitFontSmoothing: "antialiased" };

  return (
    <div style={page} data-testid="ops-page">
      <style>{`
        .ops-btn:hover { filter: brightness(1.1); }
        @keyframes opsPulse { 0%,100% { opacity:.35 } 50% { opacity:1 } }
        .ops-speaking { animation: opsPulse 1.4s ease-in-out infinite; }
        @media (max-width:840px){ .ops-grid { grid-template-columns:1fr !important; } }
      `}</style>

      <nav style={{ maxWidth: 940, margin: "0 auto", padding: "26px 28px",
        display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
        <div>
          <span style={{ fontFamily: SERIF, fontSize: 25 }}>glonet<span style={{ color: ACCENT }}>.ai</span></span>
          <span style={{ ...label, marginLeft: 12 }}>Operations</span>
        </div>
        <a href="/admin" onClick={(e) => { e.preventDefault(); navigate("/admin"); }}
           style={{ color: "#7c818b", textDecoration: "none", fontSize: 13.5 }}>Admin →</a>
      </nav>

      <main style={{ maxWidth: 940, margin: "0 auto", padding: "4px 28px 90px" }}>
        {preview && (
          <div style={{ ...label, color: AMBER, border: `1px dashed ${AMBER}`, borderRadius: 10,
            padding: "10px 14px", marginBottom: 18 }}>
            Preview — sample cards, not real data. Remove ?preview=1 for your own brief.
          </div>
        )}

        {state === "loading" && <div style={{ color: "#7c818b" }}>Working out where things stand…</div>}
        {state === "anon" && (
          <div style={{ ...card, textAlign: "center", padding: "60px 30px" }}>
            <div style={{ fontFamily: SERIF, fontSize: 28, marginBottom: 14 }}>Sign in first.</div>
            <button className="ops-btn" style={btn(true)}
                    onClick={() => navigate("/signin", { state: { from: "/ops" } })}>Sign in</button>
          </div>
        )}
        {state === "denied" && (
          <div style={{ ...card, textAlign: "center", padding: "60px 30px" }}>
            <div style={{ fontFamily: SERIF, fontSize: 28 }}>This one's for the team.</div>
            <div style={{ color: "#9a9ea7", marginTop: 10 }}>
              Your own numbers live at <a href="/affiliates/dashboard" style={{ color: ACCENT }}>
              your dashboard</a>.
            </div>
          </div>
        )}
        {state === "error" && (
          <div style={{ ...card }}>
            <div style={{ fontFamily: SERIF, fontSize: 24 }}>Couldn't build the brief.</div>
            <div style={{ color: "#9a9ea7", marginTop: 8, fontSize: 14 }}>
              Nothing is lost — try again in a moment.
            </div>
            <button className="ops-btn" style={{ ...btn(true), marginTop: 16 }} onClick={load}>Try again</button>
          </div>
        )}

        {state === "ready" && b && (
          <>
            {/* the one paragraph. Everything else is detail. */}
            <section data-testid="ops-spoken" style={{ marginBottom: 22 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
                <span style={label}>{b.phase?.label}</span>
                <span style={{ ...label, color: "#4a4e56" }}>·</span>
                <span style={{ ...label, color: "#4a4e56" }}>{b.mode} accounts only</span>
                {b.ops?.send_hold && (
                  <span style={{ ...label, color: RED, border: `1px solid ${RED}55`,
                    borderRadius: 4, padding: "2px 8px" }}>sending on hold</span>
                )}
                {b.away && (
                  <span style={{ ...label, color: AMBER }}>you're marked away</span>
                )}
              </div>
              <div style={{ fontFamily: SERIF, fontSize: 30, lineHeight: 1.3, maxWidth: "34ch" }}>
                {b.spoken.split(". ")[0]}.
              </div>
              <div style={{ color: "#b6b9c0", fontSize: 16, lineHeight: 1.6, marginTop: 10, maxWidth: "62ch" }}>
                {b.spoken.split(". ").slice(1).join(". ")}
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 18, alignItems: "center", flexWrap: "wrap" }}>
                {!noVoice && voice === "off" && (
                  <button className="ops-btn" style={btn(true)} data-testid="ops-talk"
                          onClick={startVoice}>Talk to your agent</button>
                )}
                {voice === "starting" && <span style={{ color: "#7c818b", fontSize: 14 }}>Listening…</span>}
                {voice === "live" && (
                  <>
                    <span className={speaking ? "ops-speaking" : ""}
                          style={{ ...label, color: speaking ? GREEN : "#6b6f78" }}>
                      {speaking ? "speaking" : "listening"}
                    </span>
                    <button className="ops-btn" style={btn(false)} onClick={stopVoice}>Stop</button>
                  </>
                )}
                {voice === "failed" && (
                  <span style={{ color: "#7c818b", fontSize: 13.5 }}>
                    No microphone — everything below still works.
                  </span>
                )}
                {note && <span style={{ color: GREEN, fontSize: 13.5 }}>{note}</span>}
              </div>
            </section>

            {/* the cards */}
            {shown.map((it) => (
              <Card key={it.item_key} item={it} onAct={act} busy={busy} />
            ))}
            {hidden > 0 && (
              <button className="ops-btn" data-testid="ops-more" style={{ ...btn(false), marginBottom: 18 }}
                      onClick={() => setShowAll(true)}>
                {hidden} more {hidden === 1 ? "thing" : "things"}
              </button>
            )}
            {all.length === 0 && (
              <section style={{ ...card, marginBottom: 14 }}>
                <div style={{ fontFamily: SERIF, fontSize: 24 }}>Nothing needs you.</div>
                <div style={{ color: "#9a9ea7", marginTop: 8, fontSize: 14.5 }}>
                  {b.phase?.push}
                </div>
              </section>
            )}

            {/* the report — three numbers and one sentence, never a dashboard */}
            <section data-testid="ops-report" className="ops-grid"
                     style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginTop: 22 }}>
              {[["Signups this week", b.report.signups_7d, `${b.report.referred_7d} referred`],
                ["Members", b.report.members_total, `about ${b.report.per_week}/week`],
                ["Affiliates", b.report.affiliates, `${b.report.affiliates_referring} bringing people`],
              ].map(([t, v, sub]) => (
                <div key={t} style={{ ...card, padding: "18px 20px" }}>
                  <div style={label}>{t}</div>
                  <div style={{ fontFamily: MONO, fontSize: 30, marginTop: 6 }}>{v}</div>
                  <div style={{ color: "#7c818b", fontSize: 12.5, marginTop: 4 }}>{sub}</div>
                </div>
              ))}
            </section>

            {/* The server decides whether this sentence is worth saying — one
                rule, one place, so the screen and the voice never disagree. */}
            {b.reach_line && (
              <p style={{ color: "#9a9ea7", fontSize: 14, marginTop: 14, maxWidth: "70ch" }}>
                {b.reach_line}
                {b.reach?.without_numbers > 0 && (
                  <span style={{ color: "#6b6f78" }}>
                    {" "}{b.reach.without_numbers} more never gave a number, so the real total is higher.
                  </span>
                )}
              </p>
            )}

            {/* ask Brian — a fixed list of jobs, never a free-text box */}
            {(b.jobs || []).length > 0 && (
              <section style={{ ...card, marginTop: 22 }} data-testid="ops-ask">
                <div style={label}>Need something only the database has?</div>
                {!askJob ? (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                    {b.jobs.map((j) => (
                      <button key={j.job} className="ops-btn" style={btn(false)}
                              onClick={() => setAskJob({ job: j.job, spec: j, params: {}, note: "" })}>
                        {j.label}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 15, marginBottom: 10 }}>{askJob.spec.label}</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      {(askJob.spec.params || []).map((p) => (
                        <input key={p.key} placeholder={p.label} aria-label={p.label}
                               type={p.kind === "date" ? "date" : p.kind === "int" ? "number" : "text"}
                               value={askJob.params[p.key] || ""}
                               onChange={(e) => setAskJob((a) => ({ ...a,
                                 params: { ...a.params, [p.key]: e.target.value } }))}
                               style={{ background: "rgba(255,255,255,0.05)", color: "#f1f2f4",
                                 border: "1px solid rgba(255,255,255,0.16)", borderRadius: 10,
                                 padding: "9px 12px", fontSize: 14, fontFamily: "inherit",
                                 colorScheme: "dark", minWidth: 150 }} />
                      ))}
                      <input placeholder="why (optional)" value={askJob.note} maxLength={200}
                             aria-label="why" onChange={(e) => setAskJob((a) => ({ ...a, note: e.target.value }))}
                             style={{ background: "rgba(255,255,255,0.05)", color: "#f1f2f4",
                               border: "1px solid rgba(255,255,255,0.16)", borderRadius: 10,
                               padding: "9px 12px", fontSize: 14, fontFamily: "inherit", flex: "1 1 180px" }} />
                      <button className="ops-btn" style={btn(true)}
                              onClick={() => openRequest(askJob.job, askJob.params, askJob.note || undefined)}>
                        Ask Brian
                      </button>
                      <button className="ops-btn" style={btn(false)} onClick={() => setAskJob(null)}>Cancel</button>
                    </div>
                  </div>
                )}
                <div style={{ color: "#7c818b", fontSize: 12.5, marginTop: 12, maxWidth: "72ch" }}>
                  These are set jobs, not free writing — Brian gets the exact words to run and
                  runs them himself. Anything else you need is your own Claude Code.
                </div>
              </section>
            )}

            <p style={{ color: "#6b6f78", fontSize: 12.5, marginTop: 24, maxWidth: "72ch" }}>
              Numbers are live accounts only — test accounts are never mixed in.
              Emails are counted as sent, not as delivered.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
